import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import Cantieri from "@/pages/Cantieri";
import CantiereDetail from "@/pages/CantiereDetail";
import OperaiPage from "@/pages/OperaiPage";
import Presenze from "@/pages/Presenze";
import Rendiconti from "@/pages/Rendiconti";
import Fatture from "@/pages/Fatture";
import AIReports from "@/pages/AIReports";
import OperaioHome from "@/pages/OperaioHome";
import Commercialista from "@/pages/Commercialista";
import Fornitori from "@/pages/Fornitori";
import Banche from "@/pages/Banche";
import Impostazioni from "@/pages/Impostazioni";
import Appuntamenti from "@/pages/Appuntamenti";
import Clienti from "@/pages/Clienti";
import AIAssistente from "@/pages/AIAssistente";
import Magazzino from "@/pages/Magazzino";
import ArchivioHR from "@/pages/ArchivioHR";

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "admin") return <AdminDashboard />;
  // Capocantiere e Operaio: entrambi devono timbrare come tutti
  return <OperaioHome />;
}

function AppRouter() {
  const location = useLocation();
  // Catch OAuth callback at any route (session_id arrives in URL fragment).
  // Also check window.location.hash because react-router's hash can be stale
  // after a manual history.replaceState during the Google callback flow.
  const hasOauthHash =
    (location.hash && location.hash.includes("session_id=")) ||
    (typeof window !== "undefined" && window.location.hash?.includes("session_id="));
  if (hasOauthHash) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HomeRoute />} />
        <Route path="cantieri" element={<ProtectedRoute roles={["admin", "capocantiere"]}><Cantieri /></ProtectedRoute>} />
        <Route path="cantieri/:id" element={<ProtectedRoute roles={["admin", "capocantiere", "operaio"]}><CantiereDetail /></ProtectedRoute>} />
        <Route path="operai" element={<ProtectedRoute roles={["admin"]}><OperaiPage /></ProtectedRoute>} />
        <Route path="presenze" element={<Presenze />} />
        <Route path="rendiconti" element={<ProtectedRoute roles={["admin", "capocantiere", "operaio"]}><Rendiconti /></ProtectedRoute>} />
        <Route path="fatture" element={<ProtectedRoute roles={["admin"]}><Fatture /></ProtectedRoute>} />
        <Route path="fornitori" element={<ProtectedRoute roles={["admin"]}><Fornitori /></ProtectedRoute>} />
        <Route path="banche" element={<ProtectedRoute roles={["admin"]}><Banche /></ProtectedRoute>} />
        <Route path="commercialista" element={<ProtectedRoute roles={["admin"]}><Commercialista /></ProtectedRoute>} />
        <Route path="appuntamenti" element={<ProtectedRoute roles={["admin"]}><Appuntamenti /></ProtectedRoute>} />
        <Route path="clienti" element={<ProtectedRoute roles={["admin"]}><Clienti /></ProtectedRoute>} />
        <Route path="impostazioni" element={<ProtectedRoute roles={["admin"]}><Impostazioni /></ProtectedRoute>} />
        <Route path="ai-report" element={<ProtectedRoute roles={["admin"]}><AIReports /></ProtectedRoute>} />
        <Route path="ai-assistente" element={<ProtectedRoute roles={["admin"]}><AIAssistente /></ProtectedRoute>} />
        <Route path="magazzino" element={<ProtectedRoute roles={["admin", "capocantiere"]}><Magazzino /></ProtectedRoute>} />
        <Route path="archivio-hr" element={<ProtectedRoute roles={["admin"]}><ArchivioHR /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
